import React, { useEffect, useState, useRef } from "react";
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton,
  CircularProgress, Button, Typography
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import { getEventsFromFirebase, deleteEventById, EventItem } from "../../services/eventService";
import { DocumentSnapshot } from "firebase/firestore";
import Loader from "../../Component/Loader";
import type { EventFormData } from "../../Forms/schemas/eventFormSchema"; 
import { useDispatch, UseDispatch } from "react-redux";
import { fetchEvents } from "../../redux/slice/eventlistSlice";
import { AppDispatch } from "../../redux/storage/store";

interface AdminEventTableProps {
  onEdit?: (event: EventFormData & { id: string }) => void; 
}

const AdminEventTable: React.FC<AdminEventTableProps> = ({ onEdit }) => {
  const [eventList, setEventList] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastVisible, setLastVisible] = useState<DocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [rowLoading, setRowLoading] = useState<string | null>(null);
  const isFetchingRef = useRef(false);
  const dispatch = useDispatch<AppDispatch>(); 
  

  useEffect(() => {
    fetchInitialEvents();
  }, []);

    useEffect(() => {
    dispatch(fetchEvents())
      .unwrap()
      .then((data) => {
        console.log("Fetched events:", data);
        setEventList(data);
      })
      .catch((error) => {
        console.error("Fetch error:", error);
      });
  }, [dispatch]);

  const fetchInitialEvents = async () => {
    try {
      setLoading(true);
      const { events, lastVisible } = await getEventsFromFirebase(6);
      setEventList(events);
      setLastVisible(lastVisible);
      if (events.length < 6) setHasMore(false);
    } catch {
      toast.error("Failed to load events.");
    } finally {
      setLoading(false);
    }
  };

  const fetchNextEvents = async () => {
    if (isFetchingRef.current || !hasMore) return;
    isFetchingRef.current = true;
    try {
      const { events: nextEvents, lastVisible: newLastVisible } = await getEventsFromFirebase(6, lastVisible);
      setLastVisible(newLastVisible);
      if (nextEvents.length === 0) {
        setHasMore(false);
      } else {
        setEventList((prev) => [...prev, ...nextEvents]);
      }
    } catch {
      toast.error("Failed to load more events.");
    } finally {
      isFetchingRef.current = false;
    }
  };

  const handleDelete = async (id: string) => {
    setRowLoading(id);
    try {
      await deleteEventById(id);
      toast.success("Event deleted");
      setEventList((prev) => prev.filter((data) => data.id !== id));
    } catch {
      toast.error("Failed to delete event");
    } finally {
      setRowLoading(null);
    }
  };

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Event Name</TableCell>
            <TableCell>Booth</TableCell>
            <TableCell>Location</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Tag</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow><TableCell colSpan={5}><Loader /></TableCell></TableRow>
          ) : eventList.length === 0 ? (
            <TableRow><TableCell colSpan={5}><Typography align="center">No events available</Typography></TableCell></TableRow>
          ) : (
            eventList.map((data) => (
              <TableRow key={data.id}>
                <TableCell>{data.title}</TableCell>
                <TableCell>{data.booth}</TableCell>
                <TableCell>{data.location}</TableCell>
                <TableCell>{data.date}</TableCell>
                <TableCell>{data.tag}</TableCell>
                <TableCell>
                  <IconButton
                    onClick={() =>
                      onEdit?.({
                        id: data.id,
                        title: data.title,
                        booth: data.booth,
                        location: data.location,
                        date: data.date,
                        tag: data.tag 
                      })
                    }
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(data.id)} disabled={rowLoading === data.id}>
                    {rowLoading === data.id ? <CircularProgress size={20} /> : <DeleteIcon />}
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {hasMore && (
        <div style={{ textAlign: "center", margin: "1rem 0" }}>
          <Button variant="outlined" onClick={fetchNextEvents}>Load More</Button>
        </div>
      )}
    </TableContainer>
  );
};

export default AdminEventTable;
